import _, {isUndefined} from 'lodash';
import {createSelector} from 'reselect';

import {ErrorWithData, UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {onlyItem} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {AppParametricSelector} from '../../../../helpers/reselectHelpers';
import {ConversationSortPropertiesEnum} from '../../../../models/api/conversationCommonTypes';
import {SortOptions} from '../../../../models/api/localPreferencesModel';
import {MessageSortPropertiesEnum} from '../../../../models/api/messageModel';
import {CellsSortDirectionsEnum} from '../../../../models/api/sortOptionModel';
import {
  InboxesFiltersEnum,
  InboxesUrlModel,
  RTeamFolder,
  RTeammateFolder,
  RTeammateMessageFolder,
  TeammatesFoldersEnum,
  TeammatesMessageFoldersEnum,
} from '../../../../models/url/inboxesUrlModel';
import {getSortOptions} from '../../../data/localPreferences/localPreferencesSelectors';
import {getStarredTagIds} from '../../../data/tags/tagsSelectors';
import {getUserId} from '../../../data/teammates/teammatesSelectors';
import {
  getHasFindMyDrafts2,
  getHasListSendingMessagesInSentFolder,
} from '../../../featureFlags/featureFlagsSelectors';
import {
  InboxesConversationSelectionParameters,
  InboxesConversationsParameters,
  InboxesMessagesSelectionParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesSearchParameters,
  InboxesTeammateMessagesParameters,
  InboxesUserParameters,
} from './inboxesParameters';
import {
  allExceptInboxesSelection,
  computeIsAllExceptSelection,
  emptyInboxesSelection,
  inboxesParametersSelectAllKeyword,
  normalInboxesSelection,
} from './inboxesParametersHelpers';

/*
 * Parameters parser.
 */

export type InboxesParametersSelector<P> = AppParametricSelector<P, InboxesParameters>;

export class InvalidParametersError extends ErrorWithData<InboxesParameters> {
  constructor(parameters: InboxesParameters) {
    super('The parameters are not valid.', parameters);
  }
}

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;
type InboxesParametersTemplateOf<TParameters extends InboxesParameters> = DistributiveOmit<
  TParameters,
  'sortDirection' | 'sortProperty'
>;

type InboxesUserParametersTemplate = InboxesParametersTemplateOf<InboxesUserParameters>;
export type InboxesParametersTemplate = InboxesParametersTemplateOf<InboxesParameters>;

export const getInboxesParametersParser = createSelector(
  [getUserId, getSortOptions, getStarredTagIds, getHasFindMyDrafts2, getHasListSendingMessagesInSentFolder],
  (userId, sortOptions, starredTagIds, hasFindMyDrafts2, hasListSendingMessagesInSentFolder) =>
    (url: InboxesUrlModel): InboxesParameters => {
      // Common parameters.
      const filters = url.filters && url.filters.length > 0 ? url.filters : [InboxesFiltersEnum.OPEN];
      const teamFilters =
        url.filters && url.filters.length > 0 ? url.filters : [InboxesFiltersEnum.UNASSIGNED];
      const folder = url.folder || TeammatesFoldersEnum.INBOX;

      const selectedTokens = url.selectedTokens || [];
      const selectionMessages = parseSelectedTokens(selectedTokens);
      const selectionConversations = parseSelectionIdsToNumbers(selectionMessages);

      const activityById = !isUndefined(url.activityId) ? {id: url.activityId} : undefined;
      const activityByDate = !isUndefined(url.activityDate) ? {date: url.activityDate} : undefined;
      const activity = activityById ?? activityByDate;

      const fromSelectedTokens = url.fromSelectedTokens || [];
      const fromSelectionConversations = parseSelectionIdsToNumbers(parseSelectedTokens(fromSelectedTokens));

      const search = url.searchLocation && {
        location: url.searchLocation,
        query: url.searchQuery || '',
        fromSelection: fromSelectionConversations,
      };

      const parametersTemplate = ((): InboxesParametersTemplate => {
        // Inbox parameters.
        if (!isUndefined(url.inboxId)) {
          return {
            type: InboxesParametersTypesEnum.INBOX,
            inboxId: url.inboxId,
            filters: teamFilters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // Teammate inbox parameters.
        if (!isUndefined(url.teammateInboxId)) {
          return {
            type: InboxesParametersTypesEnum.TEAMMATE_INBOX,
            inboxId: url.teammateInboxId,
            filters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // View parameters.
        if (!isUndefined(url.viewId)) {
          return {
            type: InboxesParametersTypesEnum.VIEW,
            viewId: url.viewId,
            filters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // Teammate Lens parameters.
        if (!isUndefined(url.teammateLensId)) {
          return {
            type: InboxesParametersTypesEnum.TEAMMATE_LENS,
            lensId: url.teammateLensId,
            filters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // Team Lens parameters.
        if (!isUndefined(url.teamLensId)) {
          return {
            type: InboxesParametersTypesEnum.TEAM_LENS,
            lensId: url.teamLensId,
            filters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // Teammate starred view.
        if (!isUndefined(url.teammateViewId) && starredTagIds.has(url.teammateViewId)) {
          return {
            type: InboxesParametersTypesEnum.TEAMMATE_VIEW,
            viewId: url.teammateViewId,
            filters: [InboxesFiltersEnum.OPEN, InboxesFiltersEnum.ARCHIVED, InboxesFiltersEnum.SNOOZED],
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // Teammate view parameters.
        if (!isUndefined(url.teammateViewId)) {
          return {
            type: InboxesParametersTypesEnum.TEAMMATE_VIEW,
            viewId: url.teammateViewId,
            filters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // Teammate messages parameters.
        if (!isUndefined(url.teammateId) && RTeammateMessageFolder.is(folder) && isUndefined(search)) {
          return {
            type: InboxesParametersTypesEnum.TEAMMATE_MESSAGES,
            teammateId: url.teammateId,
            folder,
            selection: selectionMessages,
            hasFindMyDrafts2,
            hasListSendingMessagesInSentFolder,
          };
        }

        // Teammate parameters.
        if (!isUndefined(url.teammateId) && folder === TeammatesFoldersEnum.ASSIGNED_SHARED) {
          return {
            type: InboxesParametersTypesEnum.TEAMMATE,
            teammateId: url.teammateId,
            folder,
            filters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // User or delegation parameters.
        if (!isUndefined(url.teammateId) && RTeammateFolder.is(folder)) {
          return {
            type: InboxesParametersTypesEnum.USER,
            teammateId: url.teammateId,
            folder,
            filters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // Team parameters.
        if (!isUndefined(url.teamId) && RTeamFolder.is(folder)) {
          return {
            type: InboxesParametersTypesEnum.TEAM,
            teamId: url.teamId,
            folder,
            filters: teamFilters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // Global parameters.
        if (url.globalFolder) {
          return {
            type: InboxesParametersTypesEnum.GLOBAL,
            folder: url.globalFolder,
            filters,
            search,
            selection: selectionConversations,
            activity,
          };
        }

        // We should never reach that point.
        throw new ErrorWithData('The provided route parameters are not valid.', {userId, url});
      })();

      // Add the sort parameters to our template.
      return buildParametersFromTemplate(sortOptions, parametersTemplate);
    },
);

export function buildParametersFromTemplate<
  TTemplate extends InboxesParametersTemplate,
  TResult extends TTemplate & InboxesParameters,
>(sortOptions: SortOptions, parametersTemplate: TTemplate & InboxesParametersTemplate): TResult {
  const sortKey = buildParametersSortKey(parametersTemplate);

  // If this is a messages list, add messages sort parameters.
  if (parametersTemplate.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
    const result: InboxesTeammateMessagesParameters = {
      ...parametersTemplate,
      ...buildTeammatesMessagesSortParameters(sortOptions, sortKey, parametersTemplate.folder),
    };
    return result as TResult;
  }

  // Otherwise, add conversations sort parameters.
  const result: InboxesConversationsParameters = {
    ...parametersTemplate,
    ...buildConversationsSortParameters(sortOptions, sortKey, parametersTemplate),
  };
  return result as TResult;
}

/*
 * Sort parameters logic.
 */

export function buildParametersSortKey(parameters: InboxesParametersTemplate) {
  // The sort order is the same for all searches.
  if (parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES && parameters.search) {
    return 'search';
  }

  const baseKey = (() => {
    switch (parameters.type) {
      case InboxesParametersTypesEnum.INBOX:
      case InboxesParametersTypesEnum.TEAMMATE_INBOX:
        return `inbox-${parameters.inboxId}`;

      case InboxesParametersTypesEnum.VIEW:
      case InboxesParametersTypesEnum.TEAMMATE_VIEW:
        return `view-${parameters.viewId}`;

      case InboxesParametersTypesEnum.TEAM_LENS:
      case InboxesParametersTypesEnum.TEAMMATE_LENS:
        return `lens-${parameters.lensId}`;

      case InboxesParametersTypesEnum.USER:
      case InboxesParametersTypesEnum.TEAMMATE:
      case InboxesParametersTypesEnum.TEAMMATE_MESSAGES:
        return `teammate-${parameters.teammateId}-${parameters.folder}`;

      case InboxesParametersTypesEnum.TEAM:
        return `team-${parameters.teamId}`;

      case InboxesParametersTypesEnum.GLOBAL:
        return `global-${parameters.folder}`;

      default:
        throw new UnreachableError(parameters);
    }
  })();

  if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
    return baseKey;
  }

  switch (onlyItem(parameters.filters)) {
    // The "all" filter gets its own sort order.
    case InboxesFiltersEnum.ALL:
      return `${baseKey}-all`;

    // The "snoozed" filter gets its own sort order.
    case InboxesFiltersEnum.SNOOZED:
      return `${baseKey}-snoozed`;

    default:
      return baseKey;
  }
}

interface InboxesParametersTemplateForSort {
  search?: InboxesSearchParameters;
  filters: ReadonlyArray<InboxesFiltersEnum>;
}

/** Build the sort properties of the inboxes parameters for a conversations screen. */
function buildConversationsSortParameters(
  sortOptions: SortOptions,
  sortKey: string,
  parameters: InboxesParametersTemplateForSort,
) {
  // If a valid sort option could not be found, return the default.
  const sortOption = sortOptions[sortKey];
  const sortProperty = sortOption?.sortProperty;
  if (!sortOption || !sortProperty) {
    return buildDefaultConversationsSortParameters(parameters);
  }

  // Allowed properties when in search are LAST_ACTIVITY_DATE and WAITING_SINCE.
  // For back-compatibility with previously saved preferences, we force the property if sortProperty is different when in search.
  const searchSortProperty =
    parameters.search &&
    ![
      ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE,
      ConversationSortPropertiesEnum.WAITING_SINCE,
    ].includes(sortProperty)
      ? ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE
      : undefined;

  // Otherwise, return the sort option we found.
  return {
    sortDirection: sortOption.sortDirection,
    sortProperty: searchSortProperty || sortProperty,
  };
}

function buildDefaultConversationsSortParameters(parameters: InboxesParametersTemplateForSort) {
  // Search.
  if (parameters.search) {
    return {
      sortDirection: CellsSortDirectionsEnum.DESCENDING,
      sortProperty: ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE,
    };
  }

  // Snoozed filter.
  if (onlyItem(parameters.filters) === InboxesFiltersEnum.SNOOZED) {
    return {
      sortDirection: CellsSortDirectionsEnum.ASCENDING,
      sortProperty: ConversationSortPropertiesEnum.EXPIRES_AT,
    };
  }

  // Everything else.
  return {
    sortDirection: CellsSortDirectionsEnum.DESCENDING,
    sortProperty: ConversationSortPropertiesEnum.BUMPED_AT,
  };
}

/** Find the sort properties of the inboxes parameters for a teammate messages screen. */
function buildTeammatesMessagesSortParameters(
  sortOptions: SortOptions,
  sortKey: string,
  folder: TeammatesMessageFoldersEnum,
) {
  // Find the sort property.
  const isDraftsFolder = folder === TeammatesMessageFoldersEnum.DRAFTS;
  const sortProperty = isDraftsFolder ? MessageSortPropertiesEnum.CREATED_AT : MessageSortPropertiesEnum.DATE;

  // Find the sort direction.
  const sortOption = sortOptions[sortKey];
  const sortDirection = sortOption?.sortDirection || CellsSortDirectionsEnum.DESCENDING;

  return {
    sortDirection,
    sortProperty,
  };
}

/*
 * Default parameters.
 */

export const getBuildDefaultInboxesParameters = createSelector(
  [getUserId, getSortOptions],
  (userId, sortOptions) =>
    (parametersTemplateOverride: Partial<InboxesUserParametersTemplate> = {}) =>
      buildParametersFromTemplate(sortOptions, {
        type: InboxesParametersTypesEnum.USER,
        teammateId: userId,
        filters: [InboxesFiltersEnum.OPEN],
        folder: TeammatesFoldersEnum.INBOX,
        selection: emptyInboxesSelection,
        ...parametersTemplateOverride,
      }),
);

export const getDefaultInboxesParameters = createSelector(
  getBuildDefaultInboxesParameters,
  (buildDefaultInboxesParameters) => buildDefaultInboxesParameters({}),
);

/*
 * Extract data from parameters.
 */

export function parseSelectedTokens(
  selectedTokens: ReadonlyArray<string>,
): InboxesMessagesSelectionParameters {
  if (selectedTokens.includes(inboxesParametersSelectAllKeyword)) {
    // we only want the tokens that are negated
    const deselectedIds = _(selectedTokens)
      .filter((token) => token.charAt(0) === '~')
      .map((token) => token.substring(1))
      .value();

    return allExceptInboxesSelection(deselectedIds);
  }

  const selectedIds = _(selectedTokens)
    .filter((token) => token !== inboxesParametersSelectAllKeyword)
    .filter((token) => token.charAt(0) !== '~')
    .value();

  return normalInboxesSelection(selectedIds);
}

export function parseSelectionIdsToNumbers(
  messagesSelection: InboxesMessagesSelectionParameters,
): InboxesConversationSelectionParameters {
  if (computeIsAllExceptSelection(messagesSelection)) {
    return allExceptInboxesSelection(messagesSelection.deselectedIds.map(Number));
  }

  return normalInboxesSelection(messagesSelection.selectedIds.map(Number));
}
